import { useEffect, useRef, useState } from 'react';

import { LoadingSpinner } from '@client/components/LoadingSpinner';

interface LoadingStage {
  message: string;
  subMessage?: string;
  duration: number; // ms before moving to next stage
}

const LOADING_STAGES: LoadingStage[] = [
  {
    message: 'Searching 500,000+ policy quotes',
    subMessage: 'Our database contains quotes from top-rated insurance carriers',
    duration: 1800,
  },
  {
    message: 'Filtering by Gender',
    subMessage: 'Matching policies to your demographic profile',
    duration: 900,
  },
  {
    message: 'Filtering by Age',
    subMessage: 'Finding age-appropriate coverage options',
    duration: 900,
  },
  {
    message: 'Filtering by Tobacco Status',
    subMessage: 'Applying health-based premium calculations',
    duration: 900,
  },
  {
    message: 'Filtering by State',
    subMessage: 'Ensuring compliance with your state regulations',
    duration: 900,
  },
  {
    message: 'Matching Policy Preferences',
    subMessage: 'Aligning with your selected coverage types',
    duration: 1200,
  },
  {
    message: 'Calculating Return on Investment',
    subMessage: 'Analyzing value across all matching policies',
    duration: 1500,
  },
  {
    message: 'Finalizing your personalized quotes',
    subMessage: 'Almost there — preparing your top recommendations',
    duration: Infinity, // Final stage - waits for data to load
  },
];

interface QuoteLoadingSequenceProps {
  isDataLoaded: boolean;
  onComplete: () => void;
}

export function QuoteLoadingSequence({ isDataLoaded, onComplete }: QuoteLoadingSequenceProps) {
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [completedStages, setCompletedStages] = useState<number[]>([]);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const hasCalledComplete = useRef(false);

  // Progress through animation stages
  useEffect(() => {
    const stage = LOADING_STAGES[currentStageIndex];
    const isFinalStage = currentStageIndex === LOADING_STAGES.length - 1;

    if (isFinalStage && !isAnimationComplete) {
      setIsAnimationComplete(true);
      return;
    }

    if (stage.duration === Infinity) {
      return;
    }

    const timer = setTimeout(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCompletedStages(prev => [...prev, currentStageIndex]);
        setCurrentStageIndex(prev => Math.min(prev + 1, LOADING_STAGES.length - 1));
        setIsTransitioning(false);
      }, 300);
    }, stage.duration);

    return () => clearTimeout(timer);
  }, [currentStageIndex, isAnimationComplete]);

  useEffect(() => {
    if (isAnimationComplete && isDataLoaded && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      setTimeout(onComplete, 2000);
    }
  }, [isAnimationComplete, isDataLoaded, onComplete]);

  const currentStage = LOADING_STAGES[currentStageIndex];
  const progress = ((currentStageIndex + 1) / LOADING_STAGES.length) * 100;

  return (
    <div className="flex h-96 items-center justify-center py-8">
      <div className="flex w-full max-w-md flex-col items-center gap-6">
        <LoadingSpinner size={60} />

        {/* Main message with transition */}
        <div
          className={`flex flex-col items-center gap-2 text-center transition-opacity duration-300 ${
            isTransitioning ? 'opacity-0' : 'opacity-100'
          }`}
        >
          <p className="text-lg font-semibold text-gray-900">{currentStage.message}</p>
          {currentStage.subMessage && (
            <p className="text-sm text-gray-700">{currentStage.subMessage}</p>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className="bg-primary h-full rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Completed stages checklist */}
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {completedStages.map(stageIndex => (
            <span
              key={stageIndex}
              className="inline-flex items-center gap-1 rounded-full bg-green-50 px-4 py-2 text-xs font-medium text-green-700"
            >
              <svg
                className="h-3 w-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              {LOADING_STAGES[stageIndex].message.replace('Filtering by ', '')}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

